import React, { useCallback, useMemo, useRef, useState } from "react";
import { getSpeechWsUrl } from "../../store/apiActions";
import { tokenManager } from "../../utils/utility.jsx";
import { devPlaygroundSttPreview, devPlaygroundTtsPreview } from "../../api/devPlaygroundAuth.js";

const STT_SAMPLE_RATE = 16000;
const TTS_SAMPLE_RATE = 24000;

// Friendlier display names for the raw provider keys the backend returns.
const PROVIDER_LABELS = {
  elevenlabs: "ElevenLabs",
  gemini: "Gemini",
};

function formatMs(ms) {
  return ms == null ? "—" : `${Math.round(ms)} ms`;
}

function floatTo16BitPCM(input) {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function int16ToFloat32(raw) {
  const f32 = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    f32[i] = raw[i] / (raw[i] < 0 ? 0x8000 : 0x7fff);
  }
  return f32;
}

/**
 * Speech-to-Text tab: records the mic and times how quickly the chosen
 * engine starts hearing and finishes transcribing. A standalone WS client
 * rather than a reuse of useVoiceUtils.js, since that hook carries
 * barge-in/keyword-detection state that has nothing to do with a quick test.
 */
function SpeechToTextTab({ modelsByProvider }) {
  const providers = useMemo(() => Object.keys(modelsByProvider || {}), [modelsByProvider]);
  const [provider, setProvider] = useState(providers?.[0] || "");
  const modelsForProvider = modelsByProvider?.[provider] || [];
  const [modelId, setModelId] = useState(modelsForProvider?.[0]?.model_id || "");
  const selected = modelsForProvider.find((m) => m.model_id === modelId) || modelsForProvider[0];

  const handleProviderChange = (next) => {
    setProvider(next);
    setModelId(modelsByProvider?.[next]?.[0]?.model_id || "");
  };

  // idle | connecting | recording | transcribing | done | error
  const [status, setStatus] = useState("idle");
  const [partialText, setPartialText] = useState("");
  const [finalText, setFinalText] = useState("");
  const [metrics, setMetrics] = useState({ connectMs: null, firstPartialMs: null, finalMs: null });
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const t0Ref = useRef(0);
  const gotFirstPartialRef = useRef(false);

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch { /* ignore */ }
      processorRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopTest = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop(); // onstop below handles the upload + cleanup
      return;
    }
    cleanupAudio();
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
    setStatus((s) => (s === "error" ? s : "done"));
  }, [cleanupAudio]);

  const startLiveTest = useCallback((micStream) => {
    const token = tokenManager.getToken("token");
    const url = getSpeechWsUrl("transcribe", "deepInsights", { stt_provider: provider });
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setErrorMsg("Couldn't connect — please try again.");
      setStatus("error");
      cleanupAudio();
      return;
    }
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setMetrics((m) => ({ ...m, connectMs: performance.now() - t0Ref.current }));
      ws.send(JSON.stringify({ token: token || null }));
      setStatus("recording");

      const audioCtx = new AudioContext({ sampleRate: STT_SAMPLE_RATE });
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(micStream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      source.connect(processor);
      processor.connect(audioCtx.destination);
      processor.onaudioprocess = (event) => {
        const pcm = floatTo16BitPCM(event.inputBuffer.getChannelData(0));
        if (ws.readyState === WebSocket.OPEN) ws.send(pcm.buffer);
      };
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!data) return;
      if (data.type === "partial") {
        if (!gotFirstPartialRef.current) {
          gotFirstPartialRef.current = true;
          setMetrics((m) => ({ ...m, firstPartialMs: performance.now() - t0Ref.current }));
        }
        setPartialText(data.text || "");
      } else if (data.type === "final") {
        setMetrics((m) => ({ ...m, finalMs: performance.now() - t0Ref.current }));
        setFinalText((prev) => (prev ? `${prev} ${data.text || ""}`.trim() : data.text || ""));
        setPartialText("");
      } else if (data.type === "error") {
        setErrorMsg(data.detail || "Something went wrong while transcribing.");
        setStatus("error");
      }
    };

    ws.onerror = () => {
      setErrorMsg("Connection error — please try again.");
      setStatus("error");
      cleanupAudio();
    };

    ws.onclose = () => {
      cleanupAudio();
      setStatus((s) => (s === "error" ? s : "done"));
    };
  }, [provider, cleanupAudio]);

  const startBatchTest = useCallback((micStream) => {
    let recorder;
    try {
      recorder = new MediaRecorder(micStream);
    } catch (err) {
      setErrorMsg("This browser can't record audio for a batch preview.");
      setStatus("error");
      cleanupAudio();
      return;
    }
    recorderRef.current = recorder;
    recordedChunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      cleanupAudio();
      const stopMs = performance.now() - t0Ref.current;
      setStatus("transcribing");
      const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || "audio/webm" });
      const token = tokenManager.getToken("token");
      const { data, error: previewErr } = await devPlaygroundSttPreview(token, blob, modelId);
      if (!data || previewErr) {
        setErrorMsg("Something went wrong while transcribing.");
        setStatus("error");
        return;
      }
      setMetrics({ connectMs: stopMs, firstPartialMs: null, finalMs: performance.now() - t0Ref.current });
      setFinalText(data.text || "");
      setStatus("done");
    };

    recorder.start();
    setMetrics((m) => ({ ...m, connectMs: performance.now() - t0Ref.current }));
    setStatus("recording");
  }, [cleanupAudio, modelId]);

  const startTest = useCallback(async () => {
    if (!provider || !selected) return;
    setErrorMsg("");
    setPartialText("");
    setFinalText("");
    setMetrics({ connectMs: null, firstPartialMs: null, finalMs: null });
    gotFirstPartialRef.current = false;
    setStatus("connecting");
    t0Ref.current = performance.now();

    let micStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: STT_SAMPLE_RATE, echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      setErrorMsg("Microphone access was denied — please allow it and try again.");
      setStatus("error");
      return;
    }
    streamRef.current = micStream;

    if (selected.kind === "batch") {
      startBatchTest(micStream);
    } else {
      startLiveTest(micStream);
    }
  }, [provider, selected, startBatchTest, startLiveTest]);

  const isActive = status === "connecting" || status === "recording" || status === "transcribing";

  return (
    <div className="voice-lab-tab-body">
      <p className="voice-lab-intro">Pick a transcription engine, click start, and speak — you'll see the text appear as you talk, plus how long each step took.</p>
      <div className="voice-lab-controls">
        <label>
          Provider
          <select value={provider} onChange={(e) => handleProviderChange(e.target.value)} disabled={isActive}>
            {providers.map((p) => (
              <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
            ))}
          </select>
        </label>
        <label>
          Model
          <select value={modelId} onChange={(e) => setModelId(e.target.value)} disabled={isActive || modelsForProvider.length < 2}>
            {modelsForProvider.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.label}{m.kind === "batch" ? " — preview (not streaming)" : " — live"}
              </option>
            ))}
          </select>
        </label>
        {!isActive ? (
          <button type="button" className="voice-lab-primary-btn" onClick={startTest}>🎤 Start talking</button>
        ) : status === "transcribing" ? (
          <button type="button" className="voice-lab-stop-btn" disabled>⏳ Transcribing…</button>
        ) : (
          <button type="button" className="voice-lab-stop-btn" onClick={stopTest}>⏹ Stop</button>
        )}
      </div>
      {selected?.kind === "batch" && (
        <p className="voice-lab-warning">⚠️ Preview only — this model doesn't stream. Speak, then hit Stop to send the whole clip and wait for one transcript.</p>
      )}
      <div className="voice-lab-transcript">
        {finalText && <div className="voice-lab-final">{finalText}</div>}
        {partialText && <div className="voice-lab-partial">{partialText}</div>}
        {!finalText && !partialText && <div className="voice-lab-placeholder">Your words will show up here while you talk…</div>}
      </div>
      {errorMsg && <p className="dev-playground-error">{errorMsg}</p>}
      <div className="voice-lab-metrics">
        {selected?.kind === "batch" ? (
          <>
            <span>Recorded: {formatMs(metrics.connectMs)}</span>
            <span>Transcribed: {formatMs(metrics.finalMs)}</span>
          </>
        ) : (
          <>
            <span>Connected: {formatMs(metrics.connectMs)}</span>
            <span>Heard you: {formatMs(metrics.firstPartialMs)}</span>
            <span>Finished transcribing: {formatMs(metrics.finalMs)}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Text-to-Speech tab: cascading Provider → Model pickers, mirroring the
 * Speech-to-Text tab, with every voice for the chosen provider shown at
 * once — live/streaming voices (fast, used in production) alongside
 * batch/preview-only voices (eleven_v3/v4/v4_turbo for ElevenLabs,
 * gemini_tts for Gemini — REST-only, can't power live conversation but
 * worth comparing quality/latency). Streams over WS for live voices, or
 * calls the REST preview endpoint for batch voices, timing each path the
 * way a tester would actually experience it.
 */
function TextToSpeechTab({ modelsByProvider }) {
  const providers = useMemo(() => Object.keys(modelsByProvider || {}), [modelsByProvider]);
  const [provider, setProvider] = useState(providers?.[0] || "");
  const modelOptions = modelsByProvider?.[provider] || [];
  const [modelId, setModelId] = useState(modelOptions?.[0]?.model_id || "");
  const selected = modelOptions.find((m) => m.model_id === modelId) || modelOptions[0];

  const handleProviderChange = (next) => {
    setProvider(next);
    setModelId(modelsByProvider?.[next]?.[0]?.model_id || "");
  };

  const [text, setText] = useState("Hello, this is a quick voice test.");
  const [status, setStatus] = useState("idle"); // idle | connecting | streaming | done | error
  const [metrics, setMetrics] = useState({ connectMs: null, firstSoundMs: null, generatedMs: null, finishedMs: null });
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const audioElRef = useRef(null);
  const t0Ref = useRef(0);
  const gotFirstByteRef = useRef(false);
  const pendingSourcesRef = useRef(0);

  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new AudioContext({ sampleRate: TTS_SAMPLE_RATE });
      nextPlayTimeRef.current = audioCtxRef.current.currentTime;
    }
    return audioCtxRef.current;
  }, []);

  const playChunk = useCallback((arrayBuffer) => {
    const ctx = ensureAudioCtx();
    const f32 = int16ToFloat32(new Int16Array(arrayBuffer));
    const buf = ctx.createBuffer(1, f32.length, TTS_SAMPLE_RATE);
    buf.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const start = Math.max(ctx.currentTime, nextPlayTimeRef.current);
    src.start(start);
    nextPlayTimeRef.current = start + buf.duration;
    pendingSourcesRef.current += 1;
    src.onended = () => {
      pendingSourcesRef.current -= 1;
      if (pendingSourcesRef.current <= 0) {
        setMetrics((m) => (m.finishedMs == null ? { ...m, finishedMs: performance.now() - t0Ref.current } : m));
        setStatus((s) => (s === "error" ? s : "done"));
      }
    };
  }, [ensureAudioCtx]);

  const stopTest = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    wsRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    if (audioElRef.current) {
      audioElRef.current.pause();
    }
    pendingSourcesRef.current = 0;
    setStatus("idle");
  }, []);

  const playLive = useCallback(() => {
    const token = tokenManager.getToken("token");
    const url = getSpeechWsUrl("tts", "deepInsights", { speed: 1.0, tts_model: selected.model_id });
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setErrorMsg("Couldn't connect — please try again.");
      setStatus("error");
      return;
    }
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      setMetrics((m) => ({ ...m, connectMs: performance.now() - t0Ref.current }));
      if (token) ws.send(JSON.stringify({ token }));
      ws.send(text);
      ws.send("");
      setStatus("streaming");
    };

    ws.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const m = JSON.parse(event.data);
          setErrorMsg(m.detail || "Something went wrong generating speech.");
        } catch {
          setErrorMsg("Something went wrong generating speech.");
        }
        setStatus("error");
        return;
      }
      if (!gotFirstByteRef.current) {
        gotFirstByteRef.current = true;
        setMetrics((m) => ({ ...m, firstSoundMs: performance.now() - t0Ref.current }));
      }
      playChunk(event.data);
    };

    ws.onerror = () => {
      setErrorMsg("Connection error — please try again.");
      setStatus("error");
    };

    ws.onclose = () => {
      setMetrics((m) => (m.generatedMs == null ? { ...m, generatedMs: performance.now() - t0Ref.current } : m));
      if (pendingSourcesRef.current <= 0) {
        setStatus((s) => (s === "error" ? s : "done"));
      }
    };
  }, [text, selected, playChunk]);

  const playBatch = useCallback(async () => {
    const token = tokenManager.getToken("token");
    const { data, error: previewErr } = await devPlaygroundTtsPreview(token, text, selected.model_id);
    if (!data || previewErr) {
      setErrorMsg("Something went wrong generating speech.");
      setStatus("error");
      return;
    }
    setMetrics((m) => ({ ...m, generatedMs: performance.now() - t0Ref.current, firstSoundMs: performance.now() - t0Ref.current }));
    if (audioElRef.current) {
      audioElRef.current.src = `data:${data.mime_type};base64,${data.audio_base64}`;
      audioElRef.current.onended = () => {
        setMetrics((m) => (m.finishedMs == null ? { ...m, finishedMs: performance.now() - t0Ref.current } : m));
        setStatus("done");
      };
      audioElRef.current.play();
    }
  }, [text, selected]);

  const playTest = useCallback(() => {
    if (!text.trim() || !selected) return;
    setErrorMsg("");
    setMetrics({ connectMs: null, firstSoundMs: null, generatedMs: null, finishedMs: null });
    gotFirstByteRef.current = false;
    pendingSourcesRef.current = 0;
    setStatus("connecting");
    t0Ref.current = performance.now();

    if (selected.kind === "live") {
      playLive();
    } else {
      playBatch();
    }
  }, [text, selected, playLive, playBatch]);

  const isBusy = status === "connecting" || status === "streaming";

  return (
    <div className="voice-lab-tab-body">
      <p className="voice-lab-intro">Pick a voice, type something, and hit play — you'll hear it out loud and see how long it took to respond.</p>
      <div className="voice-lab-controls">
        <label>
          Provider
          <select value={provider} onChange={(e) => handleProviderChange(e.target.value)} disabled={isBusy}>
            {providers.map((p) => (
              <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
            ))}
          </select>
        </label>
        <label>
          Voice
          <select value={modelId} onChange={(e) => setModelId(e.target.value)} disabled={isBusy}>
            {modelOptions.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.label}{m.kind === "batch" ? " — preview (slower)" : " — instant"}
              </option>
            ))}
          </select>
        </label>
      </div>
      {selected?.kind === "batch" && (
        <p className="voice-lab-warning">⚠️ Preview only — this voice doesn't stream, it waits for the whole clip before playing.</p>
      )}
      <textarea
        className="voice-lab-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={isBusy}
        rows={2}
        placeholder="Type something to hear it spoken…"
      />
      <div className="voice-lab-controls">
        {!isBusy ? (
          <button type="button" className="voice-lab-primary-btn" onClick={playTest}>🔊 Play</button>
        ) : (
          <button type="button" className="voice-lab-stop-btn" onClick={stopTest}>⏹ Stop</button>
        )}
      </div>
      {errorMsg && <p className="dev-playground-error">{errorMsg}</p>}
      <div className="voice-lab-metrics">
        {selected?.kind === "live" ? (
          <>
            <span>Connected: {formatMs(metrics.connectMs)}</span>
            <span>First sound: {formatMs(metrics.firstSoundMs)}</span>
            <span>Fully generated: {formatMs(metrics.generatedMs)}</span>
            <span>Done playing: {formatMs(metrics.finishedMs)}</span>
          </>
        ) : (
          <>
            <span>Generated: {formatMs(metrics.generatedMs)}</span>
            <span>Done playing: {formatMs(metrics.finishedMs)}</span>
          </>
        )}
      </div>
      <audio ref={audioElRef} />
    </div>
  );
}

/**
 * Voice Lab: a friendlier, less-technical home for trying out speech
 * engines, laid out as two separate tabs (Speech-to-Text / Text-to-Speech)
 * rather than side-by-side panels — distinct from the toolbar dropdowns
 * above, which only configure the engines used by the full chat/voice
 * flow. The Text-to-Speech tab covers every voice, live or preview-only,
 * so nothing (like eleven_v4/v4_turbo) is left out of testing.
 */
function LatencyLab({ sttModelsByProvider, ttsModelsByProvider }) {
  const [activeTab, setActiveTab] = useState("stt");

  return (
    <div className="voice-lab">
      <div className="voice-lab-tabs">
        <button
          type="button"
          className={activeTab === "stt" ? "voice-lab-tab active" : "voice-lab-tab"}
          onClick={() => setActiveTab("stt")}
        >
          🎤 Speech-to-Text
        </button>
        <button
          type="button"
          className={activeTab === "tts" ? "voice-lab-tab active" : "voice-lab-tab"}
          onClick={() => setActiveTab("tts")}
        >
          🔊 Text-to-Speech
        </button>
      </div>
      {activeTab === "stt" ? (
        <SpeechToTextTab modelsByProvider={sttModelsByProvider} />
      ) : (
        <TextToSpeechTab modelsByProvider={ttsModelsByProvider} />
      )}
    </div>
  );
}

export default LatencyLab;
